<template>
  <div>
    <multiselect
      v-model="selected"
      :track-by="config.trackBy"
      :placeholder="config.placeholder"
      :options="list"
      :custom-label="config.customLabel"
      :searchable="true"
      :show-no-results="Boolean(query)"
      :preserve-search="true"
      :internal-search="skipExternalSerching"
      :max-height="350"
      :allow-empty="allowEmpty"
      :name="name"
      :loading="loading"
      :class="{ lg: size === 'lg', ...multiselectClass }"
      :multiple="multiple"
      :taggable="taggable"
      :disabled="disabled"
      :close-on-select="closeOnSelect"
      class="search"
      open-direction="below"
      @search-change="debounceFind"
      @open="open"
      @paste.native="onPaste"
    >
      <template #afterList>
        <infinite-loading v-if="list.length" @infinite="infiniteHandler">
          <template #no-results>
            <slot name="no-result-infinite">
              <span />
            </slot>
          </template>
          <template #no-more>
            <slot name="no-more-infinite"> No more </slot>
          </template>
        </infinite-loading>
      </template>
      <template #noOptions>
        <slot name="no-options"> List is empty </slot>
      </template>
      <template #noResult>
        <slot name="no-result" />
      </template>
      <template #singleLabel="{ option }">
        <slot name="singleLabel" :option="option" />
      </template>
      <template #option="{ option }">
        <slot name="option" :option="option" />
      </template>
    </multiselect>
  </div>
</template>

<script>
import axios from "axios";
import debounce from "lodash/debounce";

import Multiselect from "vue-multiselect";

const searchOptionsDefault = {
  trackBy: "id",
  placeholder: "",
  customLabel: (e) => e,
  changeParams: (e) => e,
  changeURL: (e) => e,
  processResults: (e) => e || [],
  onListUpdate: (e) => e,
  onIncludedUpdate: (e) => e,
};

const PASTE_CHUNK_SIZE = 100;

const pasteConfigDefault = {
  paramName: "id",
  limitParamName: "limit",
  extraParams: {},
  parse: (text) => {
    if (!text) {
      return [];
    }

    const matches = text.match(/\d+/g);

    if (!matches) {
      return [];
    }

    const parsed = matches.map((value) => Number(value)).filter((value) => Number.isInteger(value));

    return parsed;
  },
  buildParams: null,
};

export default {
  components: {
    Multiselect,
  },

  props: {
    value: {
      type: [Object, Array],
      default: null,
    },
    searchUrl: {
      type: String,
      required: true,
      default: "",
    },
    size: {
      type: String,
      default: "",
    },
    multiselectClass: {
      type: Object,
      default: () => {},
    },
    openPrefetch: {
      type: Boolean,
      default: false,
    },
    renderPrefetch: {
      type: Boolean,
      default: false,
    },
    allowEmpty: {
      type: Boolean,
      default: true,
    },
    skipExternalSerching: Boolean,
    multiple: Boolean,
    taggable: Boolean,
    disabled: Boolean,
    closeOnSelect: Boolean,
    searchOptions: {
      type: Object,
      default: () => {},
    },
    name: {
      type: String,
      default: "",
    },
    enablePasteIds: {
      type: Boolean,
      default: false,
    },
    pasteConfig: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      config: searchOptionsDefault,
      list: [],
      included: [],
      query: null,
      page: 1,
      per_page: 20,
      loading: false,
      selected: null,
      pasteLoading: false,
    };
  },

  watch: {
    value: {
      handler(val) {
        this.selected = val || null;
      },
      immediate: true,
      deep: true,
    },

    selected: {
      handler(val) {
        this.$emit("input", val);
      },
      deep: true,
    },

    query() {
      if (!this.skipExternalSerching) {
        this.fetchData();
      }
    },

    list: {
      handler(data) {
        this.config.onListUpdate(data);
      },
    },
    included: {
      handler(data) {
        this.config.onIncludedUpdate(data);
      },
    },
  },

  mounted() {
    this.config = {
      ...this.config,
      ...this.searchOptions,
    };
    if (this.renderPrefetch && this.value) {
      this.fetchData({ renderPrefetch: true });
    }
  },

  methods: {
    open() {
      if (this.openPrefetch) {
        this.list = [];
        this.fetchData();
      }
    },
    onPaste(event) {
      if (!this.enablePasteIds || this.disabled || this.pasteLoading) {
        return;
      }

      const text = event?.clipboardData?.getData("text") || "";

      if (!text) {
        return;
      }

      const config = {
        ...pasteConfigDefault,
        ...this.pasteConfig,
      };

      const parsedIds =
        (typeof config.parse === "function"
          ? config.parse(text)
          : pasteConfigDefault.parse(text)) || [];

      const uniqueIds = this.getUniqueIds(parsedIds);

      if (!uniqueIds.length) {
        return;
      }

      event.preventDefault();
      this.handlePastedIds(uniqueIds, config);
    },

    getParams(opt = {}) {
      const params = {
        ...opt,
        query: this.query,
        page: this.page,
        per_page: this.per_page,
      };

      return this.config.changeParams(params);
    },

    getURL(opt = {}) {
      const params = {
        ...opt,
        query: this.query,
        page: this.page,
        per_page: this.per_page,
      };
      return this.config.changeURL(this.searchUrl, params);
    },

    getSelectedArray() {
      if (Array.isArray(this.selected)) {
        return [...this.selected];
      }

      return this.selected ? [this.selected] : [];
    },

    normalizeTrackValue(value) {
      if (value === null || value === undefined) {
        return "";
      }

      return String(value);
    },

    getUniqueIds(ids) {
      if (!Array.isArray(ids)) {
        return [];
      }

      const unique = [];
      const seen = new Set();

      ids.forEach((value) => {
        if (value === null || value === undefined) {
          return;
        }

        const key = String(value).trim();

        if (!key || seen.has(key)) {
          return;
        }

        seen.add(key);
        unique.push(value);
      });

      return unique;
    },

    chunkArray(items, size) {
      const chunks = [];

      for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
      }

      return chunks;
    },

    buildPasteParams(idsToFetch, config) {
      if (typeof config.buildParams === "function") {
        return config.buildParams(idsToFetch);
      }

      const params = {
        ...config.extraParams,
      };

      if (config.paramName) {
        params[config.paramName] = idsToFetch;
      }

      if (config.limitParamName) {
        params[config.limitParamName] = idsToFetch.length;
      }

      return params;
    },

    async handlePastedIds(ids, config) {
      const selectedArray = this.getSelectedArray();
      const trackBy = this.config.trackBy;
      const selectedValues = new Set(
        selectedArray.map((item) => this.normalizeTrackValue(item?.[trackBy]))
      );

      const duplicateIds = ids.filter((value) =>
        selectedValues.has(this.normalizeTrackValue(value))
      );

      const idsToFetch = ids.filter(
        (value) => !selectedValues.has(this.normalizeTrackValue(value))
      );

      if (!idsToFetch.length) {
        this.$emit("paste-result", {
          addedItems: [],
          fetchedItems: [],
          duplicateIds,
          missingIds: [],
          requestedIds: ids,
        });
        return;
      }

      const idChunks = this.chunkArray(idsToFetch, PASTE_CHUNK_SIZE);
      const toastPayload = {
        addedItems: [],
        fetchedItems: [],
        duplicateIds,
        missingIds: [],
        requestedIds: ids,
      };

      const idsToFetchKeys = new Set(idsToFetch.map((value) => this.normalizeTrackValue(value)));

      this.pasteLoading = true;
      this.emitLoading(true);
      this.$emit("paste-state-change", true);

      try {
        const processed = [];

        for (const chunk of idChunks) {
          const chunkParams = this.buildPasteParams(chunk, config);
          const requestParams = Array.isArray(chunkParams) ? [...chunkParams] : { ...chunkParams };
          const url = this.config.changeURL(this.searchUrl, requestParams);
          const { data } = await axios.get(url, { params: requestParams });
          processed.push(...this.config.processResults(data?.data || data || []));
        }

        const fetchedIds = new Set();

        const newItems = [];

        processed.forEach((item) => {
          const key = this.normalizeTrackValue(item?.[trackBy]);

          if (!key || !idsToFetchKeys.has(key)) {
            return;
          }

          fetchedIds.add(key);

          if (!selectedValues.has(key)) {
            selectedValues.add(key);
            newItems.push(item);
          }
        });

        toastPayload.fetchedItems = processed;

        if (newItems.length) {
          if (this.multiple || Array.isArray(this.value)) {
            const updatedSelection = [...selectedArray, ...newItems];
            this.selected = updatedSelection;
          } else {
            [this.selected] = newItems;
          }

          const listKeys = new Set(
            this.list.map((item) => this.normalizeTrackValue(item?.[trackBy]))
          );

          newItems.forEach((item) => {
            const key = this.normalizeTrackValue(item?.[trackBy]);

            if (!listKeys.has(key)) {
              this.list.push(item);
              listKeys.add(key);
            }
          });

          toastPayload.addedItems = newItems;
        }

        const missingIds = idsToFetch.filter(
          (value) => !fetchedIds.has(this.normalizeTrackValue(value))
        );
        toastPayload.missingIds = missingIds;

        this.$emit("paste-result", toastPayload);
      } catch (error) {
        this.$emit("paste-error", error);
      } finally {
        this.pasteLoading = false;
        this.emitLoading(false);
        this.$emit("paste-state-change", false);
      }
    },

    infiniteHandler($state) {
      this.page += 1;
      const url = this.getURL();
      const params = this.getParams();

      axios.get(url, { params }).then(({ data }) => {
        const res = this.config.processResults(data.data);

        if (res?.length) {
          this.list.push(...res);
          $state.loaded();
        } else {
          $state.complete();
        }
      });
    },

    emitLoading(value) {
      this.$emit("loading", value);
      this.loading = value;
    },

    debounceFind: debounce(function (q) {
      this.query = q || null;
    }, 500),

    fetchData(opt = {}) {
      this.page = 1;
      const url = this.getURL(opt);
      const params = this.getParams(opt);

      this.emitLoading(true);

      axios
        .get(url, { params })
        .then(({ data }) => {
          this.included = data.included;
          return Promise.resolve(this.config.processResults(data.data));
        })
        .then((data) => {
          this.list = data;
          if (opt.renderPrefetch) {
            [this.selected] = this.list;
          }
        })
        .finally(() => {
          this.emitLoading(false);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.search {
  ::v-deep {
    .multiselect__tags {
      border: 1px solid #ced4da;
    }
  }
  &.lg {
    ::v-deep {
      .multiselect__select {
        top: 50%;
        transform: translateY(-50%);
      }
      .multiselect--active .multiselect__select {
        top: 50%;
        transform: rotateZ(180deg) translateY(50%);
      }
      .multiselect__tags {
        font-size: 1.25rem;
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
        padding-left: 1rem;
        .multiselect__single,
        .multiselect__placeholder {
          font-size: 1.25rem;
          vertical-align: middle;
        }
        .multiselect__input {
          font-size: 1.25rem;
          margin: 0;
        }
        .multiselect__placeholder {
          padding-left: 5px;
          margin: 0;
        }
      }
    }
  }
}
</style>
